function isObjectLike(value) {
  return value !== null && typeof value === 'object';
}

/**
 * debounce https://www.educative.io/edpresso/how-to-use-the-debounce-function-in-javascript
 * @param {func} func the function to call after debounce
 * @param {integer} wait the time in ms to wait before calling func
 * @param {immediate} to call the lead function
 * @return {function} returns the executedFunction
 */
export function debounce(func, wait, immediate) {
  let timeout;

  return function executedFunction(...args) {
    const context = this;

    const later = function executedLater() {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };

    const callNow = immediate && !timeout;

    clearTimeout(timeout);

    timeout = setTimeout(later, wait);

    if (callNow) func.apply(context, args);
  };
}

/**
 * get - get a given item by path within an object
 * @param {object} obj the object to search
 * @param {string} path the path to find within the object
 * @param {string} defaultValue the default value to return if nothing is found
 * @return {value} the value that is found in the object or default
 */
export const get = (obj, path, defaultValue = undefined) => {
  if (!path) return defaultValue;
  const travel = (regexp) => String.prototype.split
    .call(path, regexp)
    .filter(Boolean)
    .reduce((res, key) => (res !== null && res !== undefined ? res[key] : res), obj);
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
  return result === undefined || result === obj ? defaultValue : result;
};

/**
 * upperFirst - converts the first case in a string to uppercase
 * @param {string} string the string to transfrom the first letter
 * @return {string} returns the converted string
 */
export const upperFirst = (string) => (string ? string.charAt(0).toUpperCase() + string.slice(1) : '');

/**
 * isEmpty - checks if an object is empty
 * @param {object} obj the object to check
 * @return {boolean} returns true or false
 */
export const isEmpty = (obj) => {
  if (typeof obj !== 'object') return true;
  return [Object, Array].includes((obj || {}).constructor) && !Object.entries((obj || {})).length;
};

/**
 * difference - checks the difference between two arrays
 * @param {array} array1 the initial array
 * @param {array} array2 the second array to check against
 * @return {array} returns the differences between the two arrays
 */
export const difference = (array1, array2) => [[...array1], [...array2]]
  .reduce((a, b) => a.filter((value) => !b.includes(value)));

/**
 * pick - pick specific items from an object
 * @param {object} object the initial object
 * @param {keys} keys the keys to pick from the object
 * @return {object} returns the picked items from the object
 */
export function pick(object, keys) {
  return keys.reduce((obj, key) => {
    const newObj = { ...obj };
    if (object && Object.prototype.hasOwnProperty.call(object, key)) {
      newObj[key] = object[key];
    }
    return newObj;
  }, {});
}

/**
 * cloneDeep - clone an object deeply https://stackoverflow.com/a/53771927/10630481
 * @param {object} obj the initial object
 * @return {object} returns the cloned object
 */
export function cloneDeep(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime());
  }

  if (obj instanceof Promise) {
    return obj;
  }

  if (obj instanceof Array) {
    return obj.reduce((arr, item, i) => {
      // eslint-disable-next-line
      arr[i] = cloneDeep(item);
      return arr;
    }, []);
  }

  if (obj instanceof Object) {
    return Object.keys(obj).reduce((newObj, key) => {
      // eslint-disable-next-line
      newObj[key] = cloneDeep(obj[key]);
      return newObj;
    }, {});
  }

  return obj;
}

/**
 * isEqual - a simple version to check if two object are equal
 * @param {object} value the first object
 * @param {object} value the second object
 * @return {boolean} returns true if both objects match otherwise false
 */
export function isEqual(value, other) {
  if (value === other) {
    return true;
  }

  if (!value || !other) {
    return false;
  }

  if (value === null || other === null || (!isObjectLike(value) && !isObjectLike(other))) {
    // eslint-disable-next-line
    return value !== value && other !== other;
  }

  const valueKeys = Object.keys(value);
  const otherKeys = Object.keys(other);

  if (valueKeys.length !== otherKeys.length) {
    return false;
  }

  if (valueKeys.length === 0 && otherKeys.length === 0) {
    return true;
  }

  const isFalse = valueKeys
    .filter((key) => {
      const v = value[key];
      const o = other[key];
      if (typeof v === 'object') return !isEqual(v, o);
      return v !== o;
    });

  if (Array.isArray(isFalse) && isFalse.length === 0) {
    return true;
  }

  return !isFalse;
}

export const kebabCase = (string) => string
  .replace(/([a-z])([A-Z])/g, '$1-$2')
  .replace(/\s+/g, '-')
  .toLowerCase();

export function camelCase(text) {
  if (!text) return text;
  const output = text.replace(/[-_\s.]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''));
  return output.substr(0, 1).toLowerCase() + output.substr(1);
}

export function ndIsNaN(value) {
  return Number.isNaN(Number(value));
}

export function isDecimal(value) {
  if (ndIsNaN(value)) return false;
  return value % 1 !== 0;
}

export function removeEmptyItems(items, key = '') {
  return items.reduce((memo, item) => {
    const itemValue = key ? get(item, key) : item;
    const idString = `${itemValue?.toString() || ''}`.trim();
    return idString ? [...memo, idString] : [...memo];
  }, []);
}

/**
 * Remove properties has no value
 * @param {object} obj Input
 * @param {object} options.isRemoveEmptyString Remove properties have empty string value
 * @param {object} options.isRemoveZero Remove properties have zero number value
 * @returns Object with all properties has value.
 */
export function deleteValuelessProperties(obj, options = {}) {
  const cloneObj = cloneDeep(obj);
  Object.entries(obj).forEach(([key, value]) => {
    const removeEmptyString = options.isRemoveEmptyString && value === '';
    const removeZeroValue = options.isRemoveZero && value === 0;
    if (value === null || value === undefined || removeEmptyString || removeZeroValue) {
      delete cloneObj[key];
    }
  });

  return cloneObj;
}

/**
 * isHashMode - return the url is in hash mode
 * @return {boolean}
 */
export function isHashMode() {
  const { href, hash } = window.location;
  const result = !!hash && href.includes('/#/');

  return result;
}

/**
 * standardizeUrlInHashMode - create new url object from window.location.hash in hash mode
 * @param {string} hash the window.location.hash
 * @return {URL object} returns the new url
 */
export function standardizeUrlInHashMode(hash) {
  if (!hash || hash.indexOf('#/') < 0) return window.location;

  const hashUrl = hash.replace(/^#\//g, '');
  const base = window.location.origin;
  return new URL(hashUrl, base);
}

/**
 * getLocationSearch - return the query search string in url
 * @return {string} return the query search string
 */
export function getLocationSearch() {
  const { search, hash } = window.location;
  if (!isHashMode()) return search;

  const newUrl = standardizeUrlInHashMode(hash);
  return newUrl?.search || '';
}

/**
 * getSearchParam - returns search param's value with the given name
 *  or undefined if not found
 * @param {String} name the search param name
 * @return {String} the value of the given name or undefined if not found
 */
export function getSearchParam(name) {
  const url = window.location.href;
  if (!url.includes(name)) return undefined;

  const search = getLocationSearch();
  if (!search) return undefined;

  const params = new URLSearchParams(search);
  const paramValue = params.get(name);
  if (!paramValue) return undefined;

  return paramValue;
}

/**
 * getCookie - returns the cookie with the given name or undefined if not found
 * @param {String} name the cookie name
 * @return {String} the decoded value of the given name or undefined if not found
 */
export function getCookie(name) {
  const matches = window.document.cookie.match(new RegExp(
    `(?:^|; )${name.replace(/([.$?*|{}()[]\\\/\+^])/g, '\\$1')}=([^;]*)`,
  ));
  return matches ? decodeURIComponent(matches[1]) : undefined;
}
